/**
 * Formats the expiry date to MM/YY format
 * @param {number} expiryMonth - The expiry month
 * @param {number} expiryYear - The expiry year
 * @returns {string} Formatted expiry date or 'N/A' if invalid
 */
export const formatExpiryDate = (expiryMonth, expiryYear) => {
  if (!expiryMonth || !expiryYear) {
    return 'N/A';
  }
  const month = expiryMonth.toString().padStart(2, '0');
  const year = expiryYear.toString().slice(-2);
  return `${month}/${year}`;
};

/**
 * Formats the card number to show only last 4 digits
 * @param {string} last4 - Last 4 digits of the card
 * @returns {string} Formatted card number or 'N/A' if invalid
 */
export const formatCardNumber = (last4) => {
  if (!last4) {
    return 'N/A';
  }
  return `***${last4}`;
};

export const SUBSCRIPTION_STATUSES = {
  ACTIVE: 'active',
  CANCELLED: 'cancelled',
  // Renewal charge failing, retries in flight. Still a membership — benefits are
  // suspended until the card is fixed, but it has not ended.
  PAST_DUE: 'past_due',
};

/**
 * How a card network is named to the member, keyed by normalizeCardBrand's ids. One
 * table for every screen that names a card (Manage membership, the saved-card renewal).
 */
export const CARD_BRAND_LABELS = Object.freeze({
  visa: 'Visa',
  amex: 'American Express',
  mastercard: 'Mastercard',
  diners: 'Diners Club',
  discover: 'Discover',
  unionpay: 'UnionPay',
  jcb: 'JCB',
});

/**
 * Card network → one of the Figma "Credit Cards" logos (12070:18673). Stripe sends
 * PaymentMethod `card.brand` ids ("amex", "diners", "unionpay"…); Shopify's
 * CustomerCreditCard.brand is free text — in production 2026-10-06 only "visa",
 * "mastercard", "american_express" and "discover".
 * Null for anything else, and the card then shows no brand at all.
 *
 * @param {string|null} brand
 * @return {string|null} visa | amex | mastercard | diners | discover | unionpay | jcb
 */
export const normalizeCardBrand = (brand) => {
  const key = String(brand || '').toLowerCase().replace(/[^a-z]/g, '');
  const aliases = {
    visa: 'visa',
    amex: 'amex',
    americanexpress: 'amex',
    mastercard: 'mastercard',
    diners: 'diners',
    dinersclub: 'diners',
    discover: 'discover',
    unionpay: 'unionpay',
    chinaunionpay: 'unionpay',
    jcb: 'jcb',
  };
  return aliases[key] || null;
};
